import './toast.css';

import CrossSvg from '@icons/cross.svg?react';
import { normalizeProps, useMachine } from '@zag-js/react';
import * as toast from '@zag-js/toast';
import { type FC, memo, type ReactNode } from 'react';

import type { ToastType } from './models/ToastEnum.ts';

interface AlertProps {
  actor: toast.Props;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parent: any;
  index: number;
  key?: string;
  dark?: boolean;
}

const Toast: FC<AlertProps> = ({ actor, parent, index }) => {
  const machineProps = {
    ...actor,
    parent: parent,
    index: index,
  };
  const service = useMachine(toast.machine, machineProps);
  const api = toast.connect(service, normalizeProps);

  const alertIcon = (type: ToastType) => {
    const alertIcons: { [key in ToastType]: ReactNode } = {
      success: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="stroke-success shrink-0 h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
        >
          <title>Success Icon</title>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
      error: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="stroke-error shrink-0 h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
        >
          <title>Error Icon</title>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
      warning: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="stroke-warning shrink-0 h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
        >
          <title>Warning Icon</title>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      ),
      info: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          className="stroke-primary shrink-0 w-6 h-6"
        >
          <title>Info Icon</title>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
      custom: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          className="stroke-primary shrink-0 w-6 h-6"
        >
          <title>Custom Icon</title>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    };

    return alertIcons[type ?? 'custom'];
  };

  return (
    <div {...api.getRootProps()}>
      <span {...api.getGhostBeforeProps()} />
      <div data-scope="toast" data-part="progressbar" />
      <div className="flex gap-3 items-center" {...api.getTitleProps()}>
        {alertIcon(api.type as ToastType)}
        <div className="flex flex-col gap-3 justify-center">
          {api.title && <h3 className="font-bold">{api.title}</h3>}
          {api.description && <div className="text-sm">{api.description}</div>}
        </div>
      </div>

      <button {...api.getCloseTriggerProps()}>
        <CrossSvg className="fill-current h-4 w-4" />
      </button>
      <span {...api.getGhostAfterProps()} />
    </div>
  );
};
export default memo(Toast);
